import { useSyncExternalStore } from 'react';
import * as Toast from '@radix-ui/react-toast';
import { Button, IconButton } from '@promanage/ui';
import { dismiss, getNotices, subscribe, type ErrorNotice } from '../lib/errorNotices';
import { Icon } from './Icon';

/**
 * Where every error in the console is drawn. Mounted once, in `main.tsx`.
 *
 * ── IN `main.tsx`, BESIDE THE ROUTER, NOT INSIDE THE SHELL ────────────────────────────────────
 *
 * The root route's `errorComponent` REPLACES the root layout when a page cannot render, so a
 * toaster mounted in the layout would vanish at exactly the moment something had gone most wrong.
 * Beside `RouterProvider` it outlives every route, including the one that failed. What that costs
 * is router context, which is why the Verify action is a prop rather than a `<Link>`.
 *
 * ── RADIX TOAST, BECAUSE OF THE DIALOGS ───────────────────────────────────────────────────────
 *
 * Roughly ten screens raise errors from inside a modal — locking a period, a commission transfer,
 * a bank file. A hand-rolled toast fails there twice over. Radix's modal marks everything outside
 * it `aria-hidden` when it opens, so a live region already on the page is silenced; and it treats
 * any pointer-down outside itself as a dismissal, so pressing a toast's close button would shut the
 * dialog and lose what the operator had typed. Radix Toast was verified against its source for
 * both: its viewport is a `DismissableLayer.Branch`, so a dialog does not count a press on it as
 * outside; and its announcement is portalled into the document at the moment of the error, after
 * the modal's sweep, so a screen reader still hears it. It shares every internal package with the
 * dialog, popover, menu and tooltip already installed — one copy, so the branch is visible to them.
 *
 * ── WHERE IT SITS ─────────────────────────────────────────────────────────────────────────────
 *
 * TOP RIGHT, under the top bar rather than over it. The top bar is where a page's own actions
 * are — the column picker, "Add bill" — and a sticky notice covering the button somebody would
 * press to try again is a notice in the way. Above every overlay in the product (`z-[80]`; the
 * phone's notification sheet is `z-[70]`), so no error is ever drawn behind a dialog.
 *
 * `type="foreground"` makes every notice ASSERTIVE: an error interrupts a screen reader rather than
 * waiting for a pause. `duration={Infinity}` makes them sticky — see `errorNotices.ts` for why a
 * money refusal must not time out. F8 moves focus to them; Escape dismisses the focused one.
 */
export function ErrorToaster({ onVerify }: { onVerify: () => void }) {
  const notices = useSyncExternalStore(subscribe, getNotices, getNotices);

  return (
    <Toast.Provider duration={Infinity} swipeDirection="right" label="Errors">
      {notices.map((notice) => (
        <Notice
          // The COUNT is in the key. A repeat of the same failure remounts the card, which is what
          // makes Radix announce it again and replay the entrance — so pressing a failing button a
          // second time is seen and heard to fail a second time, not silently absorbed.
          key={`${notice.id}:${String(notice.count)}`}
          notice={notice}
          onVerify={onVerify}
        />
      ))}

      <Toast.Viewport
        className={[
          'fixed top-[4.5rem] right-4 z-[80] m-0 flex w-[min(24rem,calc(100vw-2rem))] list-none',
          'flex-col gap-2 p-0 outline-none',
        ].join(' ')}
      />
    </Toast.Provider>
  );
}

export function Notice({ notice, onVerify }: { notice: ErrorNotice; onVerify: () => void }) {
  return (
    <Toast.Root
      type="foreground"
      open
      onOpenChange={(open) => {
        if (!open) dismiss(notice.id);
      }}
      className={[
        'error-notice glass glass-strong flex items-start gap-3 rounded-lg p-3 pr-2',
        // The danger rule down the leading edge is what says "error" at a glance, before a word is
        // read. `--danger` is reserved for true errors (tokens.css), which is what this is.
        'border-l-4 border-l-danger',
      ].join(' ')}
    >
      <Icon name="alert" size={18} className="mt-0.5 shrink-0 text-danger" />

      <div className="min-w-0 flex-1">
        <Toast.Title className="text-[0.875rem] font-semibold text-ink">
          {notice.title}
          {notice.count > 1 ? (
            // Said, not only shown as a badge — "failed 3 times" is the fact, and a bare "3" in a
            // pill beside a title reads as a count of something else.
            <span className="ml-1.5 text-[0.75rem] font-medium text-ink-muted tabular-nums">
              {`· ${String(notice.count)} times`}
            </span>
          ) : null}
        </Toast.Title>

        {notice.detail === null ? null : (
          <Toast.Description className="mt-0.5 text-[0.8125rem] whitespace-pre-line text-ink-muted">
            {/*
             * `whitespace-pre-line`, and it is the bug this whole system exists to fix: the server
             * sends several refusals joined with a newline, and fourteen of the old banners printed
             * them as one run-on sentence. Here there is exactly one place for it to be right.
             */}
            {notice.detail}
          </Toast.Description>
        )}

        {notice.reference === null ? null : (
          // Quiet and last: it is for whoever the operator reports this to, not for the operator.
          // `select-all`, so one click selects it for pasting into an email.
          <p className="mt-1 text-[0.6875rem] text-ink-muted">
            Reference{' '}
            <span className="font-mono tracking-normal select-all">{notice.reference}</span>
          </p>
        )}

        {notice.action === 'verify' ? (
          <Toast.Action altText="Verify your authenticator code" asChild>
            <Button size="sm" className="mt-2" onClick={onVerify}>
              Verify
            </Button>
          </Toast.Action>
        ) : null}
      </div>

      <Toast.Close asChild>
        <IconButton label="Dismiss" size="sm" className="shrink-0">
          <Icon name="close" size={16} />
        </IconButton>
      </Toast.Close>
    </Toast.Root>
  );
}
